import { Calendar, Car, ChevronDown, ChevronRight, MapPin, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { MatterClient } from '@/services/types/matter-client-types';
import { IncidentNote } from '@/services/types/matter-notes-types';

import { Matter } from '../entity-links';
import { HumanSummarySource } from './hooks/useHumanIncidentSummary';
import { MatterSectionCard } from './MatterSectionCard';

const SOURCE_LABELS: Record<HumanSummarySource, string> = {
  ingestion: 'Firm CMS',
  intake: 'Finch Intake',
};

interface CaseOverviewCardProps {
  client?: MatterClient;
  incidentNote?: IncidentNote | null;
  matter?: Matter;
  humanSummary?: string | null;
  humanSummarySource?: HumanSummarySource | null;
}

export const CaseOverviewCard = ({
  client,
  incidentNote,
  matter,
  humanSummary,
  humanSummarySource,
}: CaseOverviewCardProps) => {
  const [showAiSummary, setShowAiSummary] = useState(false);
  const userToggledRef = useRef(false);

  // Sync accordion state with async humanSummary prop.
  // humanSummary is null during loading; once it resolves we expand
  // the accordion only when no CMS summary exists. After the user
  // manually toggles, we stop overriding their choice.
  useEffect(() => {
    if (!userToggledRef.current) {
      setShowAiSummary(!humanSummary);
    }
  }, [humanSummary]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    try {
      const [year, month, day] = dateString.split('-').map(Number);
      if (year && month && day) {
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      }
      return dateString;
    } catch {
      return dateString;
    }
  };

  const clientDisplayName = client?.name
    || [client?.first_name, client?.last_name].filter(Boolean).join(' ')
    || null;

  const hasData = client || incidentNote?.incident_type || matter?.date_of_loss;

  if (!hasData) {
    return null;
  }

  const aiDescription = incidentNote?.description;
  const hasSummary = humanSummary || aiDescription;

  return (
    <MatterSectionCard title="Case Overview">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left column - Case details */}
        <div className="flex-shrink-0 space-y-3 lg:w-48">
          {clientDisplayName && (
            <div className="flex items-start gap-3">
              <User className="h-4 w-4 text-zinc-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-zinc-500">Client</p>
                <p className="text-xs font-medium text-zinc-900 dark:text-white">
                  {clientDisplayName}
                </p>
              </div>
            </div>
          )}

          {incidentNote?.incident_type && (
            <div className="flex items-start gap-3">
              <Car className="h-4 w-4 text-zinc-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-zinc-500">Incident Type</p>
                <p className="text-xs font-medium text-zinc-900 dark:text-white">
                  {incidentNote.incident_type}
                </p>
              </div>
            </div>
          )}

          {matter?.date_of_loss && (
            <div className="flex items-start gap-3">
              <Calendar className="h-4 w-4 text-zinc-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-zinc-500">Date of Loss</p>
                <p className="text-xs font-medium text-zinc-900 dark:text-white">
                  {formatDate(matter.date_of_loss)}
                </p>
              </div>
            </div>
          )}

          {incidentNote?.location && (
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-zinc-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-zinc-500">Location</p>
                <p className="text-xs font-medium text-zinc-900 dark:text-white">
                  {incidentNote.location}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Right column - Incident Summary */}
        {hasSummary && (
          <div className="flex-1 lg:border-l lg:border-zinc-200 dark:lg:border-zinc-800 lg:pl-6">
            {humanSummary && humanSummarySource && (
              <div>
                <div className="flex items-baseline gap-2 mb-2">
                  <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                    Incident Summary
                  </h3>
                  <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                    (from {SOURCE_LABELS[humanSummarySource]})
                  </span>
                </div>

                <p className="text-xs font-medium text-zinc-900 dark:text-white leading-tight">
                  {humanSummary}
                </p>

                {aiDescription && (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        userToggledRef.current = true;
                        setShowAiSummary(!showAiSummary);
                      }}
                      className="flex items-center gap-1 text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 transition-colors"
                    >
                      {showAiSummary ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" />
                      )}
                      {showAiSummary ? 'Hide AI Summary' : 'Show AI Summary'}
                    </button>
                    {showAiSummary && (
                      <div className="mt-2 pl-5">
                        <p className="text-xs font-medium text-zinc-700 dark:text-zinc-300 leading-tight">
                          {aiDescription}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {!humanSummary && aiDescription && (
              <div>
                <div className="flex items-baseline gap-2 mb-2">
                  <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                    Incident Summary
                  </h3>
                </div>

                <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 leading-tight">
                  Not available from Firm CMS or Finch Intake
                </p>

                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      userToggledRef.current = true;
                      setShowAiSummary(!showAiSummary);
                    }}
                    className="flex items-center gap-1 text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 transition-colors"
                  >
                    {showAiSummary ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                    {showAiSummary ? 'Hide AI Summary' : 'Show AI Summary'}
                  </button>
                  {showAiSummary && (
                    <div className="mt-2 pl-5">
                      <p className="text-xs font-medium text-zinc-700 dark:text-zinc-300 leading-tight">
                        {aiDescription}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </MatterSectionCard>
  );
};
